import { useMemo } from "react";

export type ProfileRow = {
  display_name?: string | null;
  avatar_url?: string | null;
  location?: string | null;
  stakeholder_type?: string | null;
  headline?: string | null;
  bio?: string | null;
  organization?: string | null;
  focus_areas?: string[] | null;
  counties_served?: string[] | null;
  skills?: string[] | null;
  website_url?: string | null;
  linkedin_url?: string | null;
  founded_year?: number | null;
  certifications?: string | null;
  looking_for?: string[] | null;
};

export type CompletionFieldId =
  | "display_name"
  | "avatar_url"
  | "location"
  | "stakeholder_type"
  | "headline"
  | "bio"
  | "organization"
  | "focus_areas"
  | "counties_served"
  | "skills"
  | "website_url"
  | "linkedin_url"
  | "founded_year"
  | "certifications"
  | "looking_for";

type FieldSpec = {
  id: CompletionFieldId;
  label: string;
  check: (p: ProfileRow) => boolean;
};

const nonEmpty = (v: unknown) =>
  typeof v === "string" ? v.trim().length > 0 : v != null;
const arr = (v: unknown) => Array.isArray(v) && v.length > 0;

const TIER_1: FieldSpec[] = [
  { id: "display_name", label: "Display name", check: (p) => nonEmpty(p.display_name) },
  { id: "avatar_url", label: "Profile photo", check: (p) => nonEmpty(p.avatar_url) },
  { id: "location", label: "Home county", check: (p) => nonEmpty(p.location) },
  { id: "stakeholder_type", label: "Stakeholder type", check: (p) => nonEmpty(p.stakeholder_type) },
];

const TIER_2: FieldSpec[] = [
  { id: "headline", label: "Headline", check: (p) => nonEmpty(p.headline) },
  { id: "bio", label: "Bio (120+ chars)", check: (p) => (p.bio ?? "").trim().length >= 120 },
  { id: "organization", label: "Organization", check: (p) => nonEmpty(p.organization) },
  { id: "focus_areas", label: "Focus areas", check: (p) => arr(p.focus_areas) },
  { id: "counties_served", label: "Counties served", check: (p) => arr(p.counties_served) },
];

const TIER_3: FieldSpec[] = [
  { id: "skills", label: "Skills / services", check: (p) => arr(p.skills) },
  { id: "website_url", label: "Website", check: (p) => nonEmpty(p.website_url) },
  { id: "linkedin_url", label: "LinkedIn", check: (p) => nonEmpty(p.linkedin_url) },
  { id: "founded_year", label: "Founded year", check: (p) => (p.founded_year ?? 0) > 0 },
  { id: "certifications", label: "Certifications", check: (p) => nonEmpty(p.certifications) },
  { id: "looking_for", label: "What you're looking for", check: (p) => arr(p.looking_for) },
];

export const TIER_SPECS = [TIER_1, TIER_2, TIER_3] as const;

export type TierLabel = "essentials" | "complete" | "power";
export const TIER_META: Record<
  0 | 1 | 2 | 3,
  { label: string; chipLabel: string; key: TierLabel | "none"; tone: "muted" | "sage" | "filled" | "accent" }
> = {
  0: { label: "Getting started", chipLabel: "New member", key: "none", tone: "muted" },
  1: { label: "Essentials", chipLabel: "Verified member", key: "essentials", tone: "sage" },
  2: { label: "Complete profile", chipLabel: "Complete profile", key: "complete", tone: "filled" },
  3: { label: "Power user", chipLabel: "Power user", key: "power", tone: "accent" },
};

export function useProfileCompletion(profile: ProfileRow | null | undefined) {
  return useMemo(() => {
    const p = profile ?? {};
    const tierStatus = TIER_SPECS.map((spec) => {
      const done = spec.filter((f) => f.check(p));
      const missing = spec.filter((f) => !f.check(p));
      return { spec, done, missing, complete: missing.length === 0 };
    });

    let tier: 0 | 1 | 2 | 3 = 0;
    if (tierStatus[0].complete) tier = 1;
    if (tierStatus[0].complete && tierStatus[1].complete) tier = 2;
    if (tierStatus.every((t) => t.complete)) tier = 3;

    const totalFields = TIER_SPECS.reduce((s, t) => s + t.length, 0);
    const doneFields = tierStatus.reduce((s, t) => s + t.done.length, 0);
    const percent = Math.round((doneFields / totalFields) * 100);

    const firstMissing =
      tierStatus[0].missing[0] ??
      tierStatus[1].missing[0] ??
      tierStatus[2].missing[0] ??
      null;

    return {
      tier,
      percent,
      tierStatus,
      firstMissing,
      meta: TIER_META[tier],
    };
  }, [profile]);
}